// =================================================================
// Brain Health Game Catalog
//
// Canonical source of truth for:
//   - Games available in the Brain Health program
//   - The cognitive goals each game supports
//   - The everyday-language goals residents pick from
//   - The assessment levels each game is appropriate for
//
// This file is the working copy. The same data is mirrored into
// Firebase RTDB under /brain_health/games_catalog/ for runtime
// recommendation queries — that mirror is regenerated from this
// file via the seed function (or the admin "Reseed Catalog" action).
// Never edit the Firebase copy directly.
//
// MODEL CHANGE (v2026.06.04.01), in partnership with Dr. Rob
// Winningham:
//   - Goals are now the primary organizing concept. There are five
//     goals, keyed A-E, in plain resident language.
//   - Each goal maps to a cognitive domain (for internal/clinical
//     reference and color/icon display).
//   - Games now declare an explicit `goals: ['A','D']` list instead
//     of a primary/secondary domain pair. The FIRST goal in the list
//     is the primary association (weight 2 in goal matching); the
//     rest are secondary (weight 1).
//   - The old `attention` domain was retired; its games fold into
//     "make new memories" (A) and "think faster" (D).
// =================================================================


// -----------------------------------------------------------------
// Cognitive domains
// -----------------------------------------------------------------
// Five domains based on Dr. Winningham's framework, aligned to the
// five resident goals. Each has a clinical name (internal), an
// everyday-language label (shown to residents), an emoji icon, and
// a brand color.
//
// Editing note: each domain corresponds to exactly one goal (A-E).
// Changing a domain key requires updating the matching goal below.
// -----------------------------------------------------------------

export const DOMAINS = {
  encoding: {
    key: 'encoding',
    clinical: 'Memory Encoding',
    label: 'New Memories',
    description: 'Forming and holding on to new information',
    icon: '🧠',
    color: '#690FC9',
  },
  verbal: {
    key: 'verbal',
    clinical: 'Verbal Fluency',
    label: 'Words & Communication',
    description: 'Finding words and communicating clearly',
    icon: '💬',
    color: '#33143E',
  },
  recall: {
    key: 'recall',
    clinical: 'Long-Term Recall',
    label: 'Remembering the Past',
    description: 'Retrieving things you have known for a long time',
    icon: '📖',
    color: '#161845',
  },
  speed: {
    key: 'speed',
    clinical: 'Processing Speed',
    label: 'Quick Thinking',
    description: 'Thinking and responding more rapidly',
    icon: '⚡',
    color: '#12323A',
  },
  executive: {
    key: 'executive',
    clinical: 'Executive Function',
    label: 'Reasoning',
    description: 'Reasoning, planning, and making sound decisions',
    icon: '🧩',
    color: '#B81E8F',
  },
};


// -----------------------------------------------------------------
// Resident-selectable goals
// -----------------------------------------------------------------
// Residents pick 2-3 of these during onboarding. Keyed A-E to match
// Dr. Winningham's goal sheet. Each goal maps to a domain, which the
// recommendation engine prioritizes and which supplies the icon/color.
// -----------------------------------------------------------------

export const GOALS = [
  {
    key: 'A',
    domain: 'encoding',
    label: 'Improve my ability to make new memories',
    example: '"I want to hold on to new names and faces."',
  },
  {
    key: 'B',
    domain: 'verbal',
    label: 'Improve my ability to find words and communicate',
    example: '"The word is on the tip of my tongue."',
  },
  {
    key: 'C',
    domain: 'recall',
    label: 'Improve my ability to remember things from the past that I have known',
    example: '"I want to keep my older memories sharp."',
  },
  {
    key: 'D',
    domain: 'speed',
    label: 'Improve my ability to think and respond more rapidly',
    example: '"I feel like I think slower than I used to."',
  },
  {
    key: 'E',
    domain: 'executive',
    label: 'Improve my ability to reason and make sound decisions',
    example: '"I want to keep my reasoning sharp."',
  },
];


// -----------------------------------------------------------------
// Assessment levels
// -----------------------------------------------------------------
// The cognitive assessment outputs a numeric score that maps to one
// of four levels determining difficulty pacing for games that HAVE
// difficulty modes. Games without modes are available at every level.
//
// !!! PENDING DR. WINNINGHAM — INTERIM THRESHOLDS (INTERNAL ONLY) !!!
// The assessment total is now 36 points (Stage 1 +6 recall, Stage 2
// +4 reaction +2 verbal, then -2 after removing the Checkpoint Charlie
// item per Dr. Rob). These bands map a score to a difficulty level for
// the recommendation engine. As of Dr. Rob's results-screen guidance,
// the level label and ranges are NO LONGER SHOWN to residents — the
// results screen shows only a reassuring message. So these numbers now
// drive internal game-difficulty routing only; they are not user-
// facing. Dr. Rob will still set final cut points. Proportionally
// rescaled placeholders (Brisk ~85%, Standard ~58%, Supported ~35%).
// Current total: 36 points.
// -----------------------------------------------------------------

export const LEVELS = {
  brisk: {
    key: 'brisk',
    label: 'Brisk',
    range: '30-36',
    minScore: 30,
    maxScore: 36,
    description: 'A confident pace with challenging puzzles.',
  },
  standard: {
    key: 'standard',
    label: 'Standard',
    range: '21-29',
    minScore: 21,
    maxScore: 29,
    description: 'A balanced pace with moderate challenge.',
  },
  supported: {
    key: 'supported',
    label: 'Supported',
    range: '13-20',
    minScore: 13,
    maxScore: 20,
    description: 'A gentler pace with simpler puzzles.',
  },
  guided: {
    key: 'guided',
    label: 'Guided',
    range: '< 13',
    minScore: 0,
    maxScore: 12,
    description: 'Best with staff support and group activities.',
  },
};


// -----------------------------------------------------------------
// Game catalog
// -----------------------------------------------------------------
// Each game declares:
//   - id, name, short_description, image, url (where the game lives)
//   - estimated_minutes (used for "today's session" sizing)
//   - goals: ['A','D', ...] — the goals this game supports. The FIRST
//     entry is primary (weight 2 in goal matching); the rest are
//     secondary (weight 1).
//   - levels: which assessment levels this game is appropriate for.
//     For games WITH difficulty modes, each entry maps a level to a
//     mode. For single-mode games (most board/card games), every
//     level maps to { mode: null } so the game is available to all.
//
// Games live on games.quiltt.com and are launched via absolute URL.
// Note: the `id` is a logical identifier and need not match the URL
// folder name. Word Search is an example — id 'word-search' but
// folder 'wordsearch'.
// -----------------------------------------------------------------

const QG = 'https://games.quiltt.com';

// Helper: a game available at all four levels with no difficulty mode.
const ALL_LEVELS_NO_MODE = {
  brisk: { mode: null },
  standard: { mode: null },
  supported: { mode: null },
  guided: { mode: null },
};

export const GAMES = {
  // ---- Graded games (have difficulty modes) ----

  'quick-spot': {
    id: 'quick-spot',
    name: 'Quick Spot',
    short_description:
      'Spot the odd shape out before time runs out. Sharpens speed and attention.',
    image: `${QG}/quick-spot/header.png`,
    url: `${QG}/quick-spot/`,
    estimated_minutes: 5,
    goals: ['A', 'D'],
    levels: {
      brisk: { mode: 'adaptive' },
      standard: { mode: 'adaptive' },
      supported: { mode: 'adaptive' },
      guided: { mode: 'adaptive' },
    },
  },

  'simon': {
    id: 'simon',
    name: 'Simon',
    short_description:
      'Watch the colors light up, then repeat the pattern back. The classic memory game.',
    image: `${QG}/simon/header.png`,
    url: `${QG}/simon/`,
    estimated_minutes: 5,
    goals: ['A', 'D'],
    levels: {
      brisk: { mode: 'hard' },
      standard: { mode: 'medium' },
      supported: { mode: 'easy' },
      guided: { mode: 'easy' },
    },
  },

  'daily-trivia': {
    id: 'daily-trivia',
    name: 'Daily Trivia',
    short_description:
      'Answer as many trivia questions as you can. A workout for long-term recall.',
    image: `${QG}/daily-trivia/header.png`,
    url: `${QG}/daily-trivia/`,
    estimated_minutes: 3,
    goals: ['C', 'D'],
    levels: {
      // Difficulty maps to the question pool: brisk draws from all
      // difficulties; standard from easy+moderate; supported from easy.
      // All three timed modes run the 90-second round. Guided is a
      // distinct staff-led mode: easy questions, no timer, 10 questions.
      brisk: { mode: 'hard' },
      standard: { mode: 'medium' },
      supported: { mode: 'easy' },
      guided: { mode: 'guided' },
    },
  },

  'yarn-sort': {
    id: 'yarn-sort',
    name: 'Yarn Sort',
    short_description:
      'Sort colored yarn balls into matching tubes. A relaxing reasoning puzzle.',
    image: `${QG}/yarn-sort/header.png`,
    url: `${QG}/yarn-sort/`,
    estimated_minutes: 8,
    goals: ['A', 'E'],
    levels: {
      brisk: { mode: 'hard' },
      standard: { mode: 'medium' },
      supported: { mode: 'easy' },
      guided: null,
    },
  },

  'word-twist': {
    id: 'word-twist',
    name: 'Word Twist',
    short_description:
      'Unscramble letters to find hidden words. A daily word puzzle.',
    image: `${QG}/word-twist/header.png`,
    url: `${QG}/word-twist/daily`,
    estimated_minutes: 8,
    goals: ['B'],
    levels: {
      brisk: { mode: 'hard' },
      standard: { mode: 'medium' },
      supported: { mode: 'easy' },
      guided: null,
    },
  },

  'code-breaker': {
    id: 'code-breaker',
    name: 'Code Breaker',
    short_description:
      'Decode famous quotes by matching symbols to letters. A daily cipher.',
    image: `${QG}/code-breaker/header.png`,
    url: `${QG}/code-breaker/`,
    estimated_minutes: 10,
    goals: ['B', 'D'],
    levels: {
      brisk: { mode: 'hard' },
      standard: { mode: 'medium' },
      supported: { mode: 'easy' },
      guided: null,
    },
  },

  'word-color': {
    id: 'word-color',
    name: 'Word Color',
    short_description:
      'Say the color of the word, not the word itself. A quick-thinking classic.',
    image: `${QG}/word-color/header.png`,
    url: `${QG}/word-color/`,
    estimated_minutes: 4,
    goals: ['A', 'D'],
    levels: {
      brisk: { mode: 'daily' },
      standard: { mode: 'daily' },
      supported: { mode: 'daily' },
      guided: null,
    },
  },

  'arrow-madness': {
    id: 'arrow-madness',
    name: 'Arrow Madness',
    short_description:
      'Answer the arrows as fast as you can, ignoring the distractions. A flanker-test workout for quick thinking.',
    image: `${QG}/arrow-madness/header.png`,
    url: `${QG}/arrow-madness/`,
    estimated_minutes: 3,
    goals: ['D', 'E'],
    levels: {
      // A timed reaction round. Difficulty escalates the load: easy is
      // a single arrow (choice reaction); medium adds competing
      // flankers (inhibition); hard adds a match/reverse rule that
      // switches each trial (task switching). Guided is omitted — the
      // fast pace isn't a fit for the staff-supported tier, matching
      // Word Color and our other speed games.
      brisk: { mode: 'hard' },
      standard: { mode: 'medium' },
      supported: { mode: 'easy' },
      guided: null,
    },
  },

  'critter-escape': {
    id: 'critter-escape',
    name: 'Critter Escape',
    short_description:
      'Clear the arrow blocks in the right order to free the trapped animal. A relaxing, no-timer logic puzzle.',
    image: `${QG}/critter-escape/header.png`,
    url: `${QG}/critter-escape/`,
    estimated_minutes: 6,
    goals: ['E', 'A'],
    levels: {
      // A planning puzzle: tap a block to slide it off, but only when
      // its path to the edge is clear — so the order matters. Difficulty
      // scales the board (5x5 / 6x6 / 7x7) and block count. No timer and
      // no dead ends (removing a block only frees others), so guided is a
      // fine gentle fit for staff-led sessions.
      brisk: { mode: 'hard' },
      standard: { mode: 'medium' },
      supported: { mode: 'easy' },
      guided: { mode: 'easy' },
    },
  },

  'tap-away': {
    id: 'tap-away',
    name: 'Tap Away',
    short_description:
      'Tap the arrows in the right order to slide them off the board. A soothing spatial logic puzzle.',
    image: `${QG}/tap-away/header.png`,
    url: `${QG}/tap-away/`,
    estimated_minutes: 5,
    goals: ['E', 'A'],
    levels: {
      // A "tap away" clearing puzzle with multi-cell arrow pieces.
      // Difficulty scales the board size and the length/bendiness of
      // the arrows. No timer and no dead ends (removing a piece only
      // frees others), so guided is a fine gentle fit.
      brisk: { mode: 'hard' },
      standard: { mode: 'medium' },
      supported: { mode: 'easy' },
      guided: { mode: 'easy' },
    },
  },

  'trail-maker': {
    id: 'trail-maker',
    name: 'Trail Maker',
    short_description:
      'Connect numbers and letters in order. Trains attention and quick switching.',
    image: `${QG}/trail-maker/header.png`,
    url: `${QG}/trail-maker/`,
    estimated_minutes: 6,
    goals: ['A', 'D'],
    levels: {
      brisk: { mode: 'hard' },
      standard: { mode: 'medium' },
      supported: { mode: 'easy' },
      guided: { mode: 'easy' },
    },
  },

  'sudoku': {
    id: 'sudoku',
    name: 'Sudoku',
    short_description:
      'Fill the grid so every row, column, and box contains 1–9. A daily logic puzzle.',
    image: `${QG}/sudoku/header.png`,
    url: `${QG}/sudoku/`,
    estimated_minutes: 12,
    goals: ['A', 'D', 'E'],
    levels: {
      brisk: { mode: 'hard' },
      standard: { mode: 'medium' },
      supported: { mode: 'easy' },
      guided: { mode: 'easy' },
    },
  },

  'number-sweep': {
    id: 'number-sweep',
    name: 'Number Sweep',
    short_description:
      'Clear the board by pairing numbers that match or add up to ten. A calming focus-and-speed workout.',
    image: `${QG}/number-sweep/header.png`,
    url: `${QG}/number-sweep/`,
    estimated_minutes: 7,
    goals: ['D', 'A', 'E'],
    levels: {
      // Reads ?mode=easy|medium|hard. Easy/medium are untimed with
      // built-in help (hints + generous add-ins); hard is a timed,
      // scored full board. Guided maps to easy so staff-led sessions
      // get the gentlest, no-timer setup.
      brisk: { mode: 'hard' },
      standard: { mode: 'medium' },
      supported: { mode: 'easy' },
      guided: { mode: 'easy' },
    },
  },

  'word-search': {
    id: 'word-search',
    name: 'Word Search',
    short_description:
      'Find hidden words in a letter grid. Trains focus and word recognition.',
    image: `${QG}/wordsearch/header.png`,
    url: `${QG}/wordsearch/`,
    estimated_minutes: 8,
    goals: ['A', 'B'],
    levels: {
      brisk: { mode: 'hard' },
      standard: { mode: 'medium' },
      supported: { mode: 'easy' },
      guided: { mode: 'easy' },
    },
  },

  // ---- Language Games collection ----
  // Ten word & language activities that live together in one page at
  // /language-games/ but are catalogued as separate games so the
  // recommendation engine can surface a different one each day. Each
  // deep-links into its activity via ?game=<id>; graded ones also pass
  // ?mode=<easy|medium|hard>. They lean on goal B (Words &
  // Communication), which the rest of the catalog under-serves. Images
  // are omitted so the dashboard renders each game's goal-colored icon
  // mark (the collection shares one page, not per-game headers).

  'letter-fluency': {
    id: 'letter-fluency',
    name: 'Letter Fluency Sprint',
    short_description:
      'Name as many words as you can that start with a given letter. A timed test of word-finding.',
    url: `${QG}/language-games/?game=fluency`,
    estimated_minutes: 3,
    goals: ['B', 'D'],
    levels: { ...ALL_LEVELS_NO_MODE },
  },

  'word-storms': {
    id: 'word-storms',
    name: 'Category Word Storms',
    short_description:
      'Brainstorm as many items as you can in a category before time runs out.',
    url: `${QG}/language-games/?game=wordstorm`,
    estimated_minutes: 3,
    goals: ['B', 'D'],
    levels: { ...ALL_LEVELS_NO_MODE },
  },

  'picture-it': {
    id: 'picture-it',
    name: 'Picture It',
    short_description:
      'Look at the picture and name the object. A naming and word-retrieval workout.',
    url: `${QG}/language-games/?game=pictureit`,
    estimated_minutes: 5,
    goals: ['B'],
    levels: {
      brisk: { mode: 'hard' },
      standard: { mode: 'hard' },
      supported: { mode: 'easy' },
      guided: { mode: 'easy' },
    },
  },

  'fill-the-blank': {
    id: 'fill-the-blank',
    name: 'Fill the Blank',
    short_description:
      'Finish each sentence with the word that fits. Trains word retrieval.',
    url: `${QG}/language-games/?game=fillblank`,
    estimated_minutes: 5,
    goals: ['B'],
    levels: { ...ALL_LEVELS_NO_MODE },
  },

  'odd-one-out': {
    id: 'odd-one-out',
    name: 'Odd One Out',
    short_description:
      "Find the word that doesn't belong with the others. A reasoning and categorization puzzle.",
    url: `${QG}/language-games/?game=oddone`,
    estimated_minutes: 5,
    goals: ['E', 'B'],
    levels: {
      brisk: { mode: 'hard' },
      standard: { mode: 'hard' },
      supported: { mode: 'easy' },
      guided: { mode: 'easy' },
    },
  },

  'unscramble-it': {
    id: 'unscramble-it',
    name: 'Unscramble It',
    short_description:
      'Rearrange the jumbled letters to spell the word. A spelling challenge with hints.',
    url: `${QG}/language-games/?game=unscramble`,
    estimated_minutes: 6,
    goals: ['B', 'E'],
    levels: {
      brisk: { mode: 'hard' },
      standard: { mode: 'medium' },
      supported: { mode: 'easy' },
      guided: { mode: 'easy' },
    },
  },

  'rhyme-time': {
    id: 'rhyme-time',
    name: 'Rhyme Time',
    short_description:
      'Pick every word that rhymes with the target. A playful sound-matching game.',
    url: `${QG}/language-games/?game=rhyme`,
    estimated_minutes: 4,
    goals: ['B'],
    levels: { ...ALL_LEVELS_NO_MODE },
  },

  'reorder-sentence': {
    id: 'reorder-sentence',
    name: 'Reorder the Sentence',
    short_description:
      'Put the shuffled words back into a proper sentence. Trains language and sequencing.',
    url: `${QG}/language-games/?game=reorder`,
    estimated_minutes: 6,
    goals: ['B', 'E'],
    levels: {
      brisk: { mode: 'hard' },
      standard: { mode: 'hard' },
      supported: { mode: 'easy' },
      guided: { mode: 'easy' },
    },
  },

  'spelled-backwards': {
    id: 'spelled-backwards',
    name: 'Spelled Backwards',
    short_description:
      'Choose the word spelled correctly in reverse. A spelling and working-memory twist.',
    url: `${QG}/language-games/?game=backwards`,
    estimated_minutes: 5,
    goals: ['E', 'B'],
    levels: {
      brisk: { mode: 'hard' },
      standard: { mode: 'medium' },
      supported: { mode: 'easy' },
      guided: { mode: 'easy' },
    },
  },

  'spelled-correctly': {
    id: 'spelled-correctly',
    name: 'Spelled Correctly?',
    short_description:
      'Spot the correctly spelled word of the pair. Sharpens spelling recognition.',
    url: `${QG}/language-games/?game=spellit`,
    estimated_minutes: 5,
    goals: ['B'],
    levels: {
      brisk: { mode: 'hard' },
      standard: { mode: 'medium' },
      supported: { mode: 'easy' },
      guided: { mode: 'easy' },
    },
  },

  // ---- Single-mode games (board & card; available at all levels) ----

  'memory-match': {
    id: 'memory-match',
    name: 'Memory Match',
    short_description:
      'Flip cards to find matching pairs. The classic memory game.',
    image: `${QG}/memory-match/header.png`,
    url: `${QG}/memory-match/`,
    estimated_minutes: 6,
    goals: ['A', 'D', 'E'],
    levels: {
      // Memory Match reads ?mode=<easy|medium|hard|expert> and, when
      // launched from Brain Health, auto-starts a single-player game at
      // the matching grid size (bypassing its 1-4 player / 5-grid menu):
      //   expert -> 6x5 (30 cards), hard -> 6x4 (24), medium -> 4x4 (16),
      //   easy -> 4x3 (12). Each level gets a distinct grid so residents
      //   start at their prescribed difficulty.
      brisk: { mode: 'expert' },
      standard: { mode: 'hard' },
      supported: { mode: 'medium' },
      guided: { mode: 'easy' },
    },
  },

  'battleship': {
    id: 'battleship',
    name: 'Battleship',
    short_description:
      'Place your fleet, then hunt down your opponent\u2019s ships. Play against the computer.',
    image: `${QG}/battleship/header.png`,
    url: `${QG}/battleship/`,
    estimated_minutes: 12,
    goals: ['A', 'E'],
    levels: { ...ALL_LEVELS_NO_MODE },
  },

  'yahtzee': {
    id: 'yahtzee',
    name: 'Yahtzee',
    short_description:
      'Roll the dice and build the best scoring combinations. Play solo or together.',
    image: `${QG}/yahtzee/header.png`,
    url: `${QG}/yahtzee/`,
    estimated_minutes: 15,
    goals: ['A', 'D', 'E'],
    levels: { ...ALL_LEVELS_NO_MODE },
  },

  'connect4': {
    id: 'connect4',
    name: 'Connect 4',
    short_description:
      'Drop your discs to connect four in a row. Play against the computer or a friend.',
    image: `${QG}/connect4/header.png`,
    url: `${QG}/connect4/`,
    estimated_minutes: 8,
    goals: ['A', 'D', 'E'],
    levels: { ...ALL_LEVELS_NO_MODE },
  },

  '2048': {
    id: '2048',
    name: '2048',
    short_description:
      'Slide and merge matching tiles to reach the 2048 tile. A calming number-planning puzzle.',
    image: `${QG}/2048/header.png`,
    url: `${QG}/2048/`,
    estimated_minutes: 8,
    goals: ['E', 'D', 'A'],
    levels: { ...ALL_LEVELS_NO_MODE },
  },

  'solitaire': {
    id: 'solitaire',
    name: 'Solitaire',
    short_description:
      'The timeless single-player card game. Build the stacks and clear the board.',
    image: `${QG}/solitaire/header.png`,
    url: `${QG}/solitaire/`,
    estimated_minutes: 12,
    goals: ['A', 'D', 'E'],
    levels: { ...ALL_LEVELS_NO_MODE },
  },

  'checkers': {
    id: 'checkers',
    name: 'Checkers',
    short_description:
      'Jump and capture to clear the board. Play against the computer or a friend.',
    image: `${QG}/checkers/header.png`,
    url: `${QG}/checkers/`,
    estimated_minutes: 10,
    goals: ['A', 'E'],
    levels: { ...ALL_LEVELS_NO_MODE },
  },

  'chess': {
    id: 'chess',
    name: 'Chess',
    short_description:
      'The classic game of strategy. Play against the computer or a friend.',
    image: `${QG}/chess/header.png`,
    url: `${QG}/chess/`,
    estimated_minutes: 20,
    goals: ['A', 'E'],
    levels: { ...ALL_LEVELS_NO_MODE },
  },

  'cribbage': {
    id: 'cribbage',
    name: 'Cribbage',
    short_description:
      'Count your way to 121 with cards and pegs. Play against the computer.',
    image: `${QG}/cribbage/header.png`,
    url: `${QG}/cribbage/`,
    estimated_minutes: 15,
    goals: ['A', 'E'],
    levels: { ...ALL_LEVELS_NO_MODE },
  },
};


// -----------------------------------------------------------------
// Convenience selectors
// -----------------------------------------------------------------

/**
 * All games as an array (useful for catalog views and iteration).
 * The array order is the iteration order of the GAMES object,
 * which matches the order they were added — roughly priority.
 */
export const GAMES_LIST = Object.values(GAMES);

/**
 * Look up a game by id. Returns undefined if not found.
 */
export function getGame(id) {
  return GAMES[id];
}

/**
 * Look up a goal definition by key (A-E). Returns undefined if not found.
 */
export function getGoal(key) {
  return GOALS.find((g) => g.key === key);
}

/**
 * The domain a goal maps to. Returns the domain object, or undefined.
 */
export function domainForGoal(goalKey) {
  const goal = getGoal(goalKey);
  return goal ? DOMAINS[goal.domain] : undefined;
}

/**
 * Return the games available at a given assessment level, with the
 * game-specific mode for that level resolved.
 *
 * @param {string} levelKey - "brisk" | "standard" | "supported" | "guided"
 * @returns {Array} games with `.mode` set to the resolved difficulty mode
 *                  (null for single-mode games)
 */
export function gamesForLevel(levelKey) {
  return GAMES_LIST
    .filter((game) => game.levels[levelKey] != null)
    .map((game) => ({
      ...game,
      mode: game.levels[levelKey].mode,
    }));
}

/**
 * Score how well a game matches a resident's chosen goals.
 * Primary goal (first in the game's list) scores 2; each other
 * matching goal scores 1.
 *
 * @param {Object} game - a game from GAMES
 * @param {string[]} residentGoals - selected goal keys, e.g. ['A','D']
 * @returns {number} match score (0 = no overlap)
 */
export function goalMatchScore(game, residentGoals) {
  if (!game?.goals?.length || !residentGoals?.length) return 0;
  const chosen = new Set(residentGoals);
  let score = 0;
  game.goals.forEach((goalKey, index) => {
    if (chosen.has(goalKey)) {
      score += index === 0 ? 2 : 1;
    }
  });
  return score;
}

/**
 * Recommend games for a resident given their level and chosen goals.
 * Filters to games available at the level, scores each by goal match,
 * and returns them sorted best-match-first (ties keep catalog order).
 *
 * @param {string} levelKey - assessment level key
 * @param {string[]} residentGoals - selected goal keys
 * @returns {Array} games with `.mode` and `.matchScore` set
 */
export function recommendGames(levelKey, residentGoals) {
  return gamesForLevel(levelKey)
    .map((game) => ({ ...game, matchScore: goalMatchScore(game, residentGoals) }))
    .sort((a, b) => b.matchScore - a.matchScore);
}

/**
 * Map a numeric assessment score (0-26) to a level key.
 * Returns "guided" for any score below the supported threshold,
 * including null/undefined.
 */
export function levelForScore(score) {
  if (score == null || isNaN(score)) return 'guided';
  if (score >= LEVELS.brisk.minScore) return 'brisk';
  if (score >= LEVELS.standard.minScore) return 'standard';
  if (score >= LEVELS.supported.minScore) return 'supported';
  return 'guided';
}


// -----------------------------------------------------------------
// Catalog version
// -----------------------------------------------------------------
// Bumped whenever the catalog data changes meaningfully. Used by
// the admin reseed flow to detect drift between this file and the
// Firebase copy.
// -----------------------------------------------------------------

export const CATALOG_VERSION = '2026.09.21.01';
